import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Award, Camera, CreditCard, Loader2, MapPin, Save,
  Shield, Trash2, User2, Users,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { COUNTRIES, MY_STATES } from "@/lib/countryData";

// Edit Profile — mirrors the mobile app's screen 1:1.
//
//   • Header: back arrow + "Edit Profile"
//   • Camper profile picture with Add / Remove buttons (base-64 payload
//     sent via PATCH /auth/me `avatar_camper`).
//   • MYADVENTURE PASSPORT ID card — MYAP id + Level pill (read-only).
//   • Personal Info: Full Name (required), Nick Name, Display Preference
//     segmented control, Email (read-only).
//   • Origin: Country (required) + State/Region (required — Malaysia only).
//   • Demographics: Gender + Date of Birth (native calendar picker).
//   • Consent & Privacy: consent statement + toggle (default ON — when
//     OFF the user shows as Anonymous across the app).
//   • Sticky Save Profile CTA — validates required fields client-side
//     and PATCHes the mobile backend.
export default function EditProfile() {
  const { user, api, refresh } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState(() => hydrate(user));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const fileRef = useRef(null);

  // Hydrate again if the user object appears late (initial fetch races).
  useEffect(() => {
    if (user) setForm((prev) => (prev._loaded ? prev : hydrate(user)));
  }, [user]);

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name?.trim()) e.name = "Full name is required.";
    if (!form.country) e.country = "Country is required.";
    if (form.country === "Malaysia" && !form.state) e.state = "State / Region is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    if (!validate()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        nickname: form.nickname?.trim() || "",
        display_pref: form.display_pref,
        country: form.country,
        state: form.country === "Malaysia" ? form.state : "",
        gender: form.gender || "",
        dob: form.dob || "",
        consent_shared: !!form.consent_shared,
      };
      if (form.avatarTouched) {
        payload.avatar_camper = form.avatar_camper || "";
      }
      await api.patch("/auth/me", payload);
      await refresh();
      toast.success("Profile saved.");
      navigate("/profile");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not save your profile.";
      toast.error(String(detail));
    } finally {
      setSaving(false);
    }
  };

  const onPickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Please choose an image smaller than 4 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, avatar_camper: reader.result, avatarTouched: true }));
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setForm((f) => ({ ...f, avatar_camper: "", avatarTouched: true }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const avatar = form.avatar_camper || null;
  const firstLetter = (form.name || "M").trim()[0]?.toUpperCase() || "M";

  return (
    <div className="flex flex-col gap-5 pb-32" data-testid="edit-profile-page">
      {/* Header */}
      <header className="relative flex items-center justify-center py-1">
        <Link
          to="/profile"
          aria-label="Back"
          data-testid="edit-profile-back"
          className="absolute left-0 w-10 h-10 rounded-full flex items-center justify-center transition"
          style={{ color: "var(--on-surface)" }}
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-xl md:text-2xl font-extrabold" style={{ color: "var(--on-surface)" }}>
          Edit Profile
        </h1>
      </header>

      {/* Avatar */}
      <section className="flex flex-col items-center gap-3" data-testid="edit-profile-avatar-section">
        <div
          className="w-32 h-32 rounded-full flex items-center justify-center overflow-hidden"
          style={{
            background: "var(--surface-secondary)",
            border: "2px dashed color-mix(in srgb, var(--on-surface-muted) 40%, transparent)",
          }}
        >
          {avatar ? (
            <img src={avatar} alt="" className="w-full h-full object-cover" />
          ) : (
            <User2 className="w-14 h-14" style={{ color: "var(--on-surface-muted)" }} />
          )}
        </div>
        <div className="text-[11px] font-extrabold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
          Camper profile picture
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-extrabold"
            style={{ background: "var(--brand-deep)", color: "white" }}
            data-testid="edit-profile-avatar-pick"
          >
            <Camera className="w-4 h-4" /> {avatar ? "Change Photo" : "Add Photo"}
          </button>
          {avatar && (
            <button
              type="button"
              onClick={removePhoto}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-extrabold border"
              style={{ borderColor: "var(--border-c)", color: "#B45309" }}
              data-testid="edit-profile-avatar-remove"
            >
              <Trash2 className="w-4 h-4" /> Remove
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onPickPhoto}
            data-testid="edit-profile-avatar-input"
          />
        </div>
      </section>

      {/* MYADVENTURE PASSPORT ID */}
      <Card icon={CreditCard} title="MyAdventure Passport ID" testId="edit-profile-id-card">
        <div className="text-2xl md:text-3xl font-extrabold tracking-widest font-mono" style={{ color: "var(--on-surface)" }}
             data-testid="edit-profile-myap-id">
          {user?.myap_id || "MYAP-XXXX-XXXX"}
        </div>
        <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--border-c)" }}>
          <div className="flex items-center flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-widest"
              style={{ color: "var(--premium)" }}
            >
              <Award className="w-3.5 h-3.5" /> Level / Status
            </span>
            <span
              className="ml-2.5 text-xs font-extrabold px-3 py-1 rounded-full"
              style={{ background: "var(--brand-deep)", color: "white" }}
              data-testid="edit-profile-level-pill"
            >
              Level {user?.level ?? 1}
            </span>
            <span className="ml-2 text-base font-extrabold" style={{ color: "var(--on-surface)" }}>
              {user?.title || "Newbie Camper"}
            </span>
          </div>
        </div>
      </Card>

      {/* Info cards — stack on mobile, 2-column grid on desktop so the
          wider main container is used well. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {/* Personal Info */}
      <Card icon={User2} title="Personal Info" testId="edit-profile-personal-card">
        <Field label="Full Name" required error={errors.name}>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setField("name")(e.target.value)}
            className="input"
            data-testid="edit-profile-name"
            required
          />
        </Field>
        <Field label="Nick Name">
          <input
            type="text"
            value={form.nickname}
            onChange={(e) => setField("nickname")(e.target.value)}
            className="input"
            data-testid="edit-profile-nickname"
          />
        </Field>
        <Field label="Display Preference">
          <div className="flex gap-2" data-testid="edit-profile-display-pref">
            <PrefPill
              active={form.display_pref === "full"}
              onClick={() => setField("display_pref")("full")}
              testId="edit-profile-display-pref-name"
            >
              Full Name
            </PrefPill>
            <PrefPill
              active={form.display_pref === "nick"}
              onClick={() => setField("display_pref")("nick")}
              testId="edit-profile-display-pref-nickname"
            >
              Nick Name
            </PrefPill>
          </div>
        </Field>
        <Field label="Email">
          <input
            type="email"
            value={user?.email || ""}
            disabled
            className="input"
            style={{ opacity: 0.75, cursor: "not-allowed" }}
            data-testid="edit-profile-email"
          />
        </Field>
      </Card>

      {/* Origin */}
      <Card icon={MapPin} title="Origin" testId="edit-profile-origin-card">
        <Field label="Country" required error={errors.country}>
          <select
            value={form.country}
            onChange={(e) => {
              setField("country")(e.target.value);
              if (e.target.value !== "Malaysia") setField("state")("");
            }}
            className="input"
            data-testid="edit-profile-country"
            required
          >
            <option value="">— Select country —</option>
            {COUNTRIES.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
        </Field>
        {form.country === "Malaysia" && (
          <Field label="State / Region" required error={errors.state}>
            <select
              value={form.state}
              onChange={(e) => setField("state")(e.target.value)}
              className="input"
              data-testid="edit-profile-state"
              required
            >
              <option value="">— Select state / region —</option>
              {MY_STATES.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
          </Field>
        )}
      </Card>

      {/* Demographics */}
      <Card icon={Users} title="Demographics" testId="edit-profile-demo-card">
        <Field label="Gender">
          <select
            value={form.gender}
            onChange={(e) => setField("gender")(e.target.value)}
            className="input"
            data-testid="edit-profile-gender"
          >
            <option value="">— Prefer not to say —</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </Field>
        <Field label="Date of Birth">
          <input
            type="date"
            value={form.dob || ""}
            onChange={(e) => setField("dob")(e.target.value)}
            className="input"
            max={new Date().toISOString().slice(0, 10)}
            data-testid="edit-profile-dob"
          />
        </Field>
      </Card>

      {/* Consent & Privacy */}
      <Card icon={Shield} title="Consent & Privacy" testId="edit-profile-consent-card">
        <div className="flex items-start justify-between gap-4">
          <div className="text-base font-extrabold" style={{ color: "var(--on-surface)" }}>
            Consent to share information
          </div>
          <ToggleSwitch
            on={!!form.consent_shared}
            onChange={(v) => setField("consent_shared")(v)}
            testId="edit-profile-consent-toggle"
          />
        </div>
        <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--on-surface-muted)" }}>
          I agree to share my information with the MyAdventure-Passport team and
          Campsite Owners for verification and statistical purposes. If I choose
          not to provide my consent, my account will be registered as an
          Anonymous user, and an Anonymous badge will be displayed on my profile.
          I understand that Anonymous users are not eligible to participate in
          or redeem rewards, promotions, discounts, or other member benefits
          offered by MyAdventure-Passport.
        </p>
      </Card>
      </div>

      {/* Sticky Save button (floats above the bottom nav) */}
      <div
        className="fixed left-0 right-0 z-30 px-4 md:static md:px-0 md:mt-1"
        style={{
          bottom: "calc(env(safe-area-inset-bottom) + 72px)",
        }}
      >
        {/* On mobile the sticky-save row spans the full viewport; on
            desktop it inherits the AppShell's global container width. */}
        <div className="max-w-lg mx-auto md:max-w-none">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="w-full h-14 rounded-full text-base font-extrabold inline-flex items-center justify-center gap-2 active:scale-[0.99] transition"
            style={{
              background: "var(--brand-deep)",
              color: "white",
              boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
              opacity: saving ? 0.7 : 1,
            }}
            data-testid="edit-profile-save"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save Profile</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// Turns the auth-context user into the form state. Falls back sensibly
// so a brand-new account can still open the screen without errors.
function hydrate(user) {
  return {
    _loaded: true,
    name: user?.name || user?.display_name || "",
    nickname: user?.nickname || "",
    display_pref: user?.display_pref === "nick" ? "nick" : "full",   // default: Full Name
    country: user?.country || "",
    state: user?.state || "",
    gender: user?.gender || "",
    dob: user?.dob || "",
    consent_shared: user?.consent_shared !== false,   // default ON
    avatar_camper: user?.avatar_camper || "",
    avatarTouched: false,
  };
}

function Card({ icon: Icon, title, testId, children }) {
  return (
    <section
      className="rounded-2xl p-5 md:p-6 flex flex-col gap-4"
      style={{ background: "var(--card)", border: "1px solid var(--border-c)" }}
      data-testid={testId}
    >
      <div className="inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest"
           style={{ color: "var(--brand)" }}>
        <Icon className="w-4 h-4" /> {title}
      </div>
      {children}
    </section>
  );
}

function Field({ label, required, error, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-bold" style={{ color: "var(--on-surface)" }}>
        {label}{required && <span style={{ color: "var(--premium)" }}> *</span>}
      </span>
      {children}
      {error && (
        <span className="text-xs font-bold" style={{ color: "#B45309" }} data-testid="field-error">
          {error}
        </span>
      )}
    </label>
  );
}

function PrefPill({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="px-4 h-10 rounded-full text-sm font-extrabold transition"
      style={{
        background: active ? "var(--brand-deep)" : "var(--surface-secondary)",
        color: active ? "white" : "var(--on-surface)",
      }}
    >
      {children}
    </button>
  );
}

function ToggleSwitch({ on, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      data-testid={testId}
      className="relative shrink-0 rounded-full transition"
      style={{
        width: 52,
        height: 30,
        background: on ? "var(--brand-deep)" : "color-mix(in srgb, var(--on-surface-muted) 30%, transparent)",
      }}
    >
      <span
        className="absolute top-1 rounded-full bg-white transition-all"
        style={{
          width: 22,
          height: 22,
          left: on ? 26 : 4,
          boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
        }}
      />
    </button>
  );
}
